import { ListItemText, MenuItem, Select, Typography } from "@mui/material";
import {
  useGetModelsQuery,
  useGetUserSettingQuery,
  WORD_ADDIN_AVAILABLE_MODELS_SETTING,
  WORD_ADDIN_DEFAULT_MODEL_SETTING,
} from "../api/settingsApi";
import React, { useEffect, useMemo } from "react";
import { ChatModel } from "../types/model";
import { ExpandMore } from "@mui/icons-material";

type ModelSelectProps = {
  selectedModel: ChatModel | null;
  onChange: (model: ChatModel) => void;
};

const sameIdentifier = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const ModelSelect: React.FC<ModelSelectProps> = ({ selectedModel, onChange }) => {
  const { data: models, isLoading, isError } = useGetModelsQuery();
  const { data: availableModelsSetting, isLoading: isAvailableModelsLoading } =
    useGetUserSettingQuery(WORD_ADDIN_AVAILABLE_MODELS_SETTING);
  const { data: defaultModelSetting, isLoading: isDefaultModelLoading } = useGetUserSettingQuery(
    WORD_ADDIN_DEFAULT_MODEL_SETTING
  );

  // The settings have settled once they are no longer loading — whether they
  // resolved or 404'd. Auto-selection waits for that so a configured default
  // isn't beaten to the punch by the models list arriving first.
  const areSettingsSettled = !isAvailableModelsLoading && !isDefaultModelLoading;

  // The models the organisation has allowed this add-in to use. Model
  // identifiers are stored with inconsistent casing, so compare
  // case-insensitively throughout.
  const availableModels = useMemo(() => {
    if (!models) {
      return undefined;
    }

    const allowed = availableModelsSetting?.categories;
    if (!allowed || allowed.length === 0) {
      // An empty selection means "no restriction", not "nothing allowed".
      return models;
    }

    const restricted = models.filter((model) =>
      allowed.some((option) => sameIdentifier(option.value, model.objectIdentifier))
    );

    // Nothing allowed is still in the organisation's enabled models — treat it
    // the same as unset rather than leaving the user unable to chat at all.
    return restricted.length > 0 ? restricted : models;
  }, [models, availableModelsSetting]);

  useEffect(() => {
    if (!availableModels || availableModels.length === 0 || !areSettingsSettled) {
      return;
    }

    // Re-select whenever the current choice is not among the available models —
    // this covers the initial seed, and a model disappearing mid-session because
    // it was dropped from Available Models or disabled for the region.
    const isSelectionAvailable =
      selectedModel !== null &&
      availableModels.some((model) => sameIdentifier(model.objectIdentifier, selectedModel.objectIdentifier));

    if (isSelectionAvailable) {
      return;
    }

    // Prefer the organisation's configured default, falling back to the first
    // available model when it is unset, not among the available models, or the
    // setting could not be read.
    const configuredIdentifier = defaultModelSetting?.singleCategoryIdentifier;
    const configuredModel = configuredIdentifier
      ? availableModels.find((model) => sameIdentifier(model.objectIdentifier, configuredIdentifier))
      : undefined;

    onChange(configuredModel ?? availableModels[0]);
  }, [availableModels, selectedModel, areSettingsSettled, defaultModelSetting, onChange]);

  const handleChange = (selectedObjectIdentifier: string) => {
    const model = availableModels?.find((m) => m.objectIdentifier === selectedObjectIdentifier);
    if (model) {
      onChange(model);
    }
  };

  if (availableModels) {
    return (
      <Select
        size="small"
        value={selectedModel?.objectIdentifier || ""}
        onChange={(e) => handleChange(e.target.value)}
        IconComponent={ExpandMore}
        renderValue={(value) => {
          const model = availableModels.find((m) => m.objectIdentifier === value);
          if (!model) return "";
          return (
            <Typography variant="body2" noWrap>
              {model.name}
            </Typography>
          );
        }}
      >
        {availableModels.map((model) => (
          <MenuItem key={model.objectIdentifier} value={model.objectIdentifier}>
            <ListItemText
              primary={model.name}
              secondary={model.hostedBy}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (isLoading) {
    return <div>Loading models...</div>;
  }

  if (isError) {
    return <div>Error loading models</div>;
  }

  return null;
};

export default ModelSelect;
